"use client";

import { useEffect, useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

type AppDialogSize = "sm" | "md" | "lg" | "xl";

type AppDialogProps = {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
  onClose: () => void;
  size?: AppDialogSize;
  closeOnOverlayClick?: boolean;
  mobileFullscreen?: boolean;
  fullscreen?: boolean;
  showCloseButton?: boolean;
  className?: string;
};

const sizeClass: Record<AppDialogSize, string> = {
  sm: "max-w-[420px]",
  md: "max-w-[560px]",
  lg: "max-w-[800px]",
  xl: "max-w-[1000px]",
};

export function AppDialog({
  open,
  title,
  description,
  children,
  actions,
  onClose,
  size = "md",
  closeOnOverlayClick = true,
  mobileFullscreen = false,
  fullscreen = false,
  showCloseButton = true,
  className,
}: AppDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }

    previousActiveElementRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timer = window.setTimeout(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const firstFocusable = dialog.querySelector<HTMLElement>(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
      );
      if (firstFocusable) {
        firstFocusable.focus();
      } else {
        dialog.focus();
      }
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousActiveElementRef.current?.focus?.();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const backdropClass = "fixed inset-0 z-50 flex overflow-y-auto bg-black/50 backdrop-blur-[2px]";

  return (
    <div
      className={cn(
        fullscreen
          ? `${backdropClass} items-stretch justify-center`
          : mobileFullscreen
          ? `${backdropClass} items-stretch justify-center sm:items-start sm:px-4 sm:py-6`
          : `${backdropClass} dialog-mobile-sheet-wrap items-end justify-center px-0 py-0 sm:items-start sm:px-4 sm:py-6`,
      )}
      onClick={() => {
        if (closeOnOverlayClick) {
          onClose();
        }
      }}
    >

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          "relative flex flex-col overflow-hidden border border-[var(--border)] bg-[var(--surface)]",
          fullscreen
            ? "h-[100dvh] w-full max-h-[100dvh] rounded-none"
            : mobileFullscreen
            ? "h-[100dvh] w-full max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[90vh] sm:rounded-[12px]"
            : "dialog-mobile-sheet w-full max-h-[90dvh] sm:max-h-[90vh] sm:rounded-[12px]",
          !fullscreen && !mobileFullscreen ? sizeClass[size] : "",
          className,
        )}
        style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.10)" }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-[var(--border)] bg-[var(--surface)] px-5 py-4">
          <div>
            <h2 id={titleId} className="text-[15px] font-semibold text-[var(--foreground)]">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-0.5 text-[12px] text-[var(--muted-foreground)]">
                {description}
              </p>
            ) : null}
          </div>
          {showCloseButton ? (
            <button
              type="button"
              className="rounded-[5px] p-1 text-[var(--muted-foreground)] transition hover:bg-[var(--surface-muted)] hover:text-[var(--foreground)]"
              onClick={onClose}
              aria-label="Fechar modal"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {/* Body */}
        <div
          className={
            fullscreen || mobileFullscreen
              ? "flex min-h-0 flex-1 flex-col overflow-hidden"
              : "flex max-h-[calc(90vh-64px)] flex-col overflow-hidden"
          }
        >
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>
          {actions ? (
            <div className="sticky bottom-0 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-[var(--border)] bg-[var(--surface)] px-5 py-[14px]">
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
